/**
 * Shopify Product Customizer — OOP Refactor
 *
 * Architecture: Service Layer + Facade with Dependency Injection
 *
 * CustomizerApp          — Facade: wires services together, owns lifecycle
 *   StepManager          — Step navigation state machine
 *   SelectionStore       — Single source of truth for variant selections (EventTarget)
 *   DomController        — Isolates ALL jQuery/DOM reads and writes
 *   VariantResolver      — Pure functions: match variants by option arrays
 *   MappingEngine        — Cross-product auto-select rules (pluggable strategies)
 *   PriceCalculator      — Pure pricing logic
 *   CartService          — Shopify AJAX cart I/O
 *   ThreeDService        — 3D configurator + AR bridge
 */

"use strict";

// ─────────────────────────────────────────────────────────────────────────────
// VariantResolver — pure, no DOM, unit-testable
// ─────────────────────────────────────────────────────────────────────────────
class VariantResolver {
  /**
   * Return the first variant whose options[] matches selectedOptions exactly.
   * @param {Array}  variants        — product.variants array
   * @param {Array}  selectedOptions — e.g. ['Red', 'Large']
   * @returns {Object|null}
   */
  findByOptions(variants, selectedOptions) {
    if (!Array.isArray(selectedOptions) || !selectedOptions.length) return null;
    return (
      variants.find((v) =>
        selectedOptions.every((opt, i) => v.options[i] === opt)
      ) ?? null
    );
  }

  findIdByOptions(variants, selectedOptions) {
    return this.findByOptions(variants, selectedOptions)?.id ?? null;
  }

  /**
   * Look up a variant across ALL products by id, attaching product_id.
   * @param {Array}  productsData
   * @param {number} variantId
   * @returns {Object|null}
   */
  findVariantGlobally(productsData, variantId) {
    for (const product of productsData) {
      const variant = product.variants.find((v) => v.id === variantId);
      if (variant) {
        return { ...variant, product_id: product.id };
      }
    }
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PriceCalculator — pure, no DOM
// ─────────────────────────────────────────────────────────────────────────────
class PriceCalculator {
  /**
   * Sum regular prices for all available variants.
   * @param {Array} variants
   * @returns {number} total in cents
   */
  calcRegular(variants) {
    return variants.reduce((sum, v) => {
      return v.available && v.price ? sum + parseInt(v.price, 10) : sum;
    }, 0);
  }

  /**
   * Sum compare-at prices (fallback to regular) for all available variants.
   * @param {Array} variants
   * @returns {number} total in cents
   */
  calcCompareAt(variants) {
    return variants.reduce((sum, v) => {
      if (!v.available) return sum;
      const price = v.compare_at_price
        ? parseInt(v.compare_at_price, 10)
        : parseInt(v.price, 10);
      return sum + (price || 0);
    }, 0);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SelectionStore — event-driven single source of truth
//
// Fires CustomEvents so any subscriber (analytics, wishlist, etc.) can react
// without touching core classes — this is the Open/Closed seam.
//
// Events dispatched on the store instance (an EventTarget):
//   'change'  — whenever selected variant ids or the main variant change
// ─────────────────────────────────────────────────────────────────────────────
class SelectionStore extends EventTarget {
  #variantIds = [];
  #mainVariantId = null;

  get variantIds() {
    return [...this.#variantIds];
  }

  get mainVariantId() {
    return this.#mainVariantId;
  }

  /**
   * Replace the full selection state and fire 'change'.
   * @param {number[]} ids
   * @param {number|null} mainId
   */
  update(ids, mainId) {
    this.#variantIds = [...ids];
    this.#mainVariantId = mainId;
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { variantIds: this.variantIds, mainVariantId: mainId },
      })
    );
  }

  reset() {
    this.update([], null);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DomController — isolates ALL jQuery / DOM interaction
//
// Any future migration from jQuery to vanilla JS or a framework only touches
// this class.
// ─────────────────────────────────────────────────────────────────────────────
class DomController {
  // ── Selectors (centralised, not scattered) ──────────────────────────────

  get $modal() {
    return $("#customizer-2");
  }
  get $openBtns() {
    return $(".openModalBtn");
  }
  get $closeBtn() {
    return $("#closeModalBtn");
  }
  get $backBtn() {
    return $("#customiser-back-btn");
  }
  get $nextBtn() {
    return $("#customiser-add-to-cart");
  }
  get $body() {
    return $("body");
  }
  get $html() {
    return $("html");
  }
  get $titleHeading() {
    return $("#customiser-steps-title h3");
  }
  get $reviewList() {
    return $("ul[review-order-list]");
  }

  // ── Read next-button label attributes ──────────────────────────────────

  getNextBtnTexts() {
    const $b = this.$nextBtn;
    return {
      next: $b.attr("btn2-text-next"),
      review: $b.attr("btn2-text-review"),
      addToCart: $b.attr("btn2-text-add-to-cart"),
    };
  }

  // ── Modal open / close ──────────────────────────────────────────────────

  openModal() {
    this.$modal.css("display", "block");
    this.$body.addClass("customiser-visible");
    this.$html.attr("style", "overflow: hidden; padding-right: 15px;");
  }

  closeModal() {
    this.$modal.css("display", "none");
    this.$body
      .removeClass("customiser-visible")
      .removeClass("minicart-visible");
    this.$html.removeAttr("style");
  }

  // ── Step visibility ─────────────────────────────────────────────────────

  showStep(step) {
    $("div[customizer-step][customiser-product-container-step]").addClass(
      "hidden"
    );
    $(
      `div[customizer-step='${step}'][customiser-product-container-step]`
    ).removeClass("hidden");
  }

  isStepHidden(step) {
    return !!$(
      `div[customiser-product-container][customiser-product-container-step][customizer-step='${step}']`
    ).attr("hidden-product");
  }

  // ── Back / Next button state ────────────────────────────────────────────

  showBackBtn() {
    this.$backBtn.removeClass("hidden");
  }
  hideBackBtn() {
    this.$backBtn.addClass("hidden");
  }
  setNextBtnText(text) {
    this.$nextBtn.text(text);
  }

  // ── Step title ──────────────────────────────────────────────────────────

  getStepTitles(step) {
    const titles = [];
    $(
      `div[customiser-product-container][customizer-product='true'][customizer-step='${step}']`
    )
      .children("fieldset[customiser-option-container]")
      .each(function () {
        const t = $(this).attr("customiser-step-title");
        if (t && !titles.includes(t)) titles.push(t);
      });
    return titles;
  }

  getReviewListTitle() {
    return this.$reviewList.attr("customiser-step-title") ?? "";
  }

  setTitle(text) {
    this.$titleHeading.text(text);
  }

  // ── Product containers ──────────────────────────────────────────────────

  /**
   * Returns an array of { productId, title, step, crossLevel, isHidden, $el }
   * for every customiser product container.
   */
  getProductContainers() {
    const results = [];
    $("div[customiser-product-container][customizer-product='true']").each(
      function () {
        const $el = $(this);
        results.push({
          productId: $el.attr("product-id"),
          title: $el.attr("title"),
          step: $el.attr("customizer-step"),
          crossLevel: $el.attr("cross-product-level"),
          isHidden: $el.hasClass("hidden"),
          hasHiddenAttr: !!$el.attr("hidden-product"),
          $el,
        });
      }
    );
    return results;
  }

  // ── Option selections ───────────────────────────────────────────────────

  /**
   * Read all selected option values for a product container element.
   * @param {jQuery} $productEl
   * @returns {string[]}
   */
  getSelectedOptionsForProduct($productEl) {
    const selected = [];
    $productEl
      .children("fieldset[customiser-option-container]")
      .each(function () {
        const $checked = $(this).find(
          "div[customiser-option-item][class*=checked]"
        );
        if ($checked.length) selected.push($checked.attr("data-value"));
      });
    return selected;
  }

  /**
   * Read all option fieldsets for a product container, returning metadata.
   * @param {jQuery} $productEl
   * @returns {Array}
   */
  getOptionFieldsets($productEl) {
    const fieldsets = [];
    $productEl
      .children("fieldset[customiser-option-container]")
      .each(function () {
        const $fs = $(this);
        const $checked = $fs.find(
          "div[customiser-option-item][class*=checked]"
        );
        fieldsets.push({
          optionName: $fs.attr("option-name"),
          dataValue: $checked.length ? $checked.attr("data-value") : "",
          $fieldset: $fs,
        });
      });
    return fieldsets;
  }

  /**
   * Mark a single option item as checked, unchecking siblings.
   * @param {jQuery} $item
   */
  setChecked($item) {
    const productId = $item.attr("product-id");
    const optionName = $item.attr("option-name");
    const position = $item.attr("data-option-position");
    $(
      `div[customiser-option-item][data-option-position='${position}'][option-name='${optionName}'][product-id='${productId}']`
    )
      .removeClass("checked")
      .attr("checked_data", "false");
    $item.addClass("checked").attr("checked_data", "true");
  }

  // ── Price display ───────────────────────────────────────────────────────

  setTotalPrice(regularFormatted, compareFormatted) {
    $("p.customiser-total-price span").text(regularFormatted);
    $("p.customiser-total-price s").text(
      compareFormatted && compareFormatted !== regularFormatted
        ? compareFormatted
        : ""
    );
  }

  // ── Option item price display ───────────────────────────────────────────

  /**
   * Update the price labels on option items for a given product.
   * @param {string}  productId
   * @param {Array}   variants  — product variants array from data
   * @param {Array}   selectedOptions
   * @param {number}  optionCount  — 1 or 2
   */
  updateOptionItemPrices(productId, variants, selectedOptions, optionCount) {
    const fmt = window.formatMoney2;
    if (!fmt) return;

    if (optionCount === 2) {
      for (let i = 0; i < 2; i++) {
        const j = 1 - i;
        const sel = selectedOptions[i];
        const sel2 = selectedOptions[j];
        const filtered = variants.filter((v) => sel === v.options[i]);
        if (!filtered.length || !sel2) continue;
        filtered.forEach((v) => {
          const $el = $(
            `div[customiser-option-item][product-id='${productId}'][data-value='${v.options[j]}']`
          );
          if (!$el.length) return;
          $el.attr("item-price2", v.price);
          $el.find("span.cp_variant_price").text(v.price ? fmt(v.price) : "");
          $el
            .find("s.compare_at_price")
            .text(v.compare_at_price ? fmt(v.compare_at_price) : "");
        });
      }
    } else if (optionCount === 1) {
      variants.forEach((v) => {
        const $el = $(
          `div[customiser-option-item][product-id='${productId}'][data-value='${v.option1}']`
        );
        if (!$el.length) return;
        $el.attr("item-price", v.price);
        $el.find("span.cp_variant_price").text(v.price ? fmt(v.price) : "");
        $el
          .find("s.compare_at_price")
          .text(v.compare_at_price ? fmt(v.compare_at_price) : "");
      });
    }
  }

  // ── Review order list ───────────────────────────────────────────────────

  updateReviewListItem(productId, optionName, step, dataValue, hide) {
    const $item = $(
      `ul[review-order-list] li[product-id='${productId}'][option-name='${optionName}']`
    );
    if (!$item.length) return;
    hide ? $item.addClass("hidden") : $item.removeClass("hidden");
    $item.find("button[customiser-step]").attr("customiser-step", step);
    $item.find("p[data-value]").text(dataValue);
  }

  // ── Product show / hide ─────────────────────────────────────────────────

  hideProduct($productEl) {
    const productId = $productEl.attr("product-id");
    $productEl.addClass("hidden").attr("hidden-product", true);
    $productEl
      .find(`div[customiser-option-item][product-id='${productId}']`)
      .removeClass("checked")
      .attr("checked_data", "false");
  }

  showProduct($productEl, currentStep) {
    const productId = $productEl.attr("product-id");
    const productStep = $productEl.attr("customizer-step");
    if (productStep == currentStep) $productEl.removeClass("hidden");
    $productEl.removeAttr("hidden-product");

    $productEl
      .children(
        "fieldset[customiser-option-container]:not(.hide_custom_cross_product_variant)"
      )
      .each(function () {
        const $items = $(this).find(
          `div[customiser-option-item][product-id='${productId}']`
        );
        if ($items.filter(".checked").length === 0 && $items.length > 0) {
          $($items[0]).addClass("checked").attr("checked_data", "true");
        }
      });
  }

  // ── URL ──────────────────────────────────────────────────────────────────

  updateURL(mainVariantId, allVariantIds = []) {
    if (!mainVariantId) return;
    const url = new URL(window.location.href);
    const paramValue = [
      mainVariantId,
      ...allVariantIds.filter((id) => id !== mainVariantId),
    ].join(",");
    if (url.searchParams.get("variant") !== paramValue) {
      url.searchParams.set("variant", paramValue);
      window.history.replaceState({}, "", url.toString());
    }
  }

  // ── 3D configurator ─────────────────────────────────────────────────────

  getConfigurator() {
    return document.getElementById("configurator2");
  }

  getARButton() {
    return document.querySelector(".new-customiser-ar-btn");
  }

  getDimensionsButton() {
    return document.getElementById("dimensionsBtn");
  }
  // ── Bind helpers ─────────────────────────────────────────────────────────

  onOptionItemClick(handler) {
    $("div[customiser-option-item]").on("click", function () {
      handler($(this));
    });
  }

  onOpenModal(handler) {
    this.$openBtns.on("click", handler);
  }

  onCloseModal(handler) {
    this.$closeBtn.on("click", handler);
  }

  onBackBtn(handler) {
    this.$backBtn.on("click", handler);
  }

  onNextBtn(handler) {
    this.$nextBtn.on("click", handler);
  }

  onReviewEditBtnClick(handler) {
    $("ul[review-order-list] button[change-customiser-step]").on(
      "click",
      function () {
        const step = Number($(this).attr("customiser-step"));
        handler(step);
      }
    );
  }

  onRemoveFromCart(handler) {
    $("a[remove-product]").on("click", function () {
      handler($(this).attr("product-id"));
    });
  }

  // Rebind remove-from-cart buttons (cart drawer re-renders them)
  rebindRemoveFromCart(handler) {
    $("a[remove-product]")
      .off("click")
      .on("click", function () {
        handler($(this).attr("product-id"));
      });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// StepManager — step navigation state machine
// ─────────────────────────────────────────────────────────────────────────────
class StepManager {
  #current = 0;
  #max;

  constructor(maxSteps) {
    this.#max = maxSteps;
  }

  get current() {
    return this.#current;
  }
  get max() {
    return this.#max;
  }
  get isFirst() {
    return this.#current === 0;
  }
  get isLast() {
    return this.#current === this.#max;
  }
  get isPreLast() {
    return this.#current === this.#max - 1;
  }

  advance() {
    if (this.#current < this.#max) this.#current++;
  }

  back() {
    if (this.#current > 0) this.#current--;
  }

  jumpTo(step) {
    this.#current = step;
  }

  reset() {
    this.#current = 0;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MappingEngine — cross-product auto-select rules
//
// To add a new mapping strategy, extend BaseMappingStrategy and register it.
// ─────────────────────────────────────────────────────────────────────────────
class BaseMappingStrategy {
  /**
   * @param {Object} productMapping  — one entry from product_json.mapping[]
   * @param {Object} productsData    — full products array
   * @param {DomController} dom
   */
  // eslint-disable-next-line no-unused-vars
  apply(productMapping, productsData, dom) {
    throw new Error("apply() must be implemented by subclass");
  }
}

class DefaultMappingStrategy extends BaseMappingStrategy {
  apply(productMapping, productsData, dom) {
    const mainProduct = productsData.find((p) => p.main_product === true);
    if (!mainProduct) return;

    const crossProductId = productMapping.cross_product.id;
    const mainVariantName = productMapping.main_product_variant_name;

    let selectedValue = $(
      `fieldset[customiser-option-container][product-id='${mainProduct.id}'][option-name=${mainVariantName}] div.label[class*=checked]`
    ).attr("data-value");

    if (selectedValue) selectedValue = selectedValue.toLowerCase();
    if (!selectedValue) selectedValue = "'140x200'";

    const mappings = productMapping.mapping ?? [];
    for (const entry of mappings) {
      const [from, to] = entry.split("=");
      if (from.toLowerCase() !== selectedValue) continue;
      $(
        `fieldset[customiser-option-container][product-id=${crossProductId}] > div.label`
      ).each(function () {
        if ($(this).attr("data-value") === to) {
          dom.setChecked($(this));
        }
      });
      break;
    }
  }
}

class MappingEngine {
  #strategy;

  constructor(strategy = new DefaultMappingStrategy()) {
    this.#strategy = strategy;
  }

  /** Swap the active strategy at runtime (e.g. for a new mapping algorithm). */
  setStrategy(strategy) {
    this.#strategy = strategy;
  }

  /**
   * Run auto-select for all products that have mapping rules.
   * @param {Array} productsData
   * @param {DomController} dom
   */
  applyAll(productsData, dom) {
    productsData.forEach((product) => {
      product.mapping?.forEach((mapping) => {
        this.#strategy.apply(mapping, productsData, dom);
      });
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ThreeDService — 3D configurator + AR bridge
// ─────────────────────────────────────────────────────────────────────────────
class ThreeDService {
  #configurator;

  constructor(configurator) {
    this.#configurator = configurator;
  }

  /** Dispatch variant:change for every selected variant id. */
  syncVariants(variantIds) {
    if (!this.#configurator || !variantIds.length) return;
    variantIds.forEach((id) => {
      document.dispatchEvent(
        new CustomEvent("variant:change", { detail: { variantId: id } })
      );
    });
  }

  /** Set the initial variant attribute before first render. */
  setInitialVariants(variantIds) {
    if (!this.#configurator || !variantIds.length) return;
    this.#configurator.setAttribute("initialVariantId", variantIds);
  }

  /** Toggle Dimensions button */
  toggleDimensions() {
    const cfg = this.#configurator;
    if (!cfg) return;

    if (typeof cfg.ensureConfiguratorElement === "function") {
      cfg.ensureConfiguratorElement().toggleDimensions();
    }
  }

  /** Toggle AR view — tries multiple APIs for compatibility. */
  toggleAR() {
    const cfg = this.#configurator;
    if (!cfg) return;
    if (typeof cfg.ensureConfiguratorElement === "function") {
      cfg.ensureConfiguratorElement().toggleAR?.();
    } else if (typeof cfg.toggleAR === "function") {
      cfg.toggleAR();
    } else if (typeof cfg.arToggle === "function") {
      cfg.arToggle();
    } else if (typeof cfg.modelViewer?.toggleAR === "function") {
      cfg.modelViewer.toggleAR();
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CartService — Shopify AJAX cart I/O
// ─────────────────────────────────────────────────────────────────────────────
class CartService {
  /**
   * Add all selected variants to the Shopify cart via liquidAjaxCart.
   * @param {number[]}  variantIds     — ordered list of selected variant ids
   * @param {number}    mainVariantId
   * @param {Array}     productsData
   * @param {VariantResolver} resolver
   * @param {Function}  onSuccess      — called after cart update
   * @param {Function}  onError
   */
  async addToCart(
    variantIds,
    mainVariantId,
    productsData,
    resolver,
    onSuccess,
    onError
  ) {
    if (!variantIds.length) return;

    const customizationId = parseInt(
      new Date().getTime().toString().substr(4),
      10
    );
    const mainProduct = productsData.find((p) => p.main_product);
    const mainVariant = mainProduct?.variants?.find(
      (variant) => String(variant.id) === String(mainVariantId)
    );
    const bedLabel = [mainProduct?.title, mainVariant?.title]
      .filter((value) => value && value !== "Default Title")
      .join(" · ");

    // Build enriched cart_items
    const cartItems = [];
    variantIds.forEach((id, index) => {
      const varData = resolver.findVariantGlobally(productsData, id);
      if (!varData || varData.variant_no_data) return;
      varData.cart_item_sort = index;
      varData.properties = { _c_id: customizationId };
      if (varData.product_id === mainProduct.id) {
        varData.properties._is_main_product = true;
      } else {
        varData.properties._main_product_id = mainProduct.id;
      }
      cartItems.push(varData);
    });

    // Sort lowest first, build liquidAjaxCart items payload
    cartItems.sort((a, b) => a.cart_item_sort - b.cart_item_sort);

    const items = [
      {
        id: mainVariantId,
        quantity: 1,
        properties: { _is_main_product: "true", _cart_item_sort: 0 },
      },
    ];

    // The main product already uses sort 0. Number only accessories that are added.
    let nextCartItemSort = 1;
    cartItems.forEach((c) => {
      if (c.id === mainVariantId) return;
      items.push({
        id: c.id,
        quantity: 1,
        properties: {
          _cart_item_sort: nextCartItemSort++,
          _main_product_id: Number(mainVariantId),
          ...(bedLabel ? { _For: bedLabel } : {}),
        },
      });
    });

    // Shopify prepends the batch in reverse order. Send options last-to-first
    // so checkout shows the bed first, then its customiser selections.
    window.liquidAjaxCart.add(
      { items: [...items].reverse() },
      {
        lastCallback: (response) => {
          if (!response.responseData?.ok) {
            onError?.(response);
            return;
          }

          onSuccess?.(response);

          setTimeout(() => {
            document.dispatchEvent(new CustomEvent("cart:updated"));
          }, 500);
        },
      }
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomizerApp — Facade that wires everything together
// ─────────────────────────────────────────────────────────────────────────────
class CustomizerApp {
  #productsData;
  #stepManager;
  #selectionStore;
  #dom;
  #resolver;
  #mappingEngine;
  #priceCalc;
  #cartService;
  #threeDService;
  #nextBtnTexts;

  // True when the page was loaded with ?ar=true — updateURL is suppressed
  // on modal open and only fires once the user manually changes a selection.
  #arMode = false;

  constructor({
    productsData,
    stepManager,
    selectionStore,
    dom,
    resolver,
    mappingEngine,
    priceCalc,
    cartService,
    threeDService,
  }) {
    this.#productsData = productsData;
    this.#stepManager = stepManager;
    this.#selectionStore = selectionStore;
    this.#dom = dom;
    this.#resolver = resolver;
    this.#mappingEngine = mappingEngine;
    this.#priceCalc = priceCalc;
    this.#cartService = cartService;
    this.#threeDService = threeDService;
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  init() {
    this.#nextBtnTexts = this.#dom.getNextBtnTexts();
    this.#arMode =
      new URL(window.location.href).searchParams.get("ar") === "true";
    this.#bindEvents();
    this.#refreshAll();
    if (this.#arMode) {
      this.#onOpenModal();
    }
  }

  // ── Event wiring ──────────────────────────────────────────────────────────

  #bindEvents() {
    const dom = this.#dom;

    dom.onOpenModal(() => this.#onOpenModal());
    dom.onCloseModal(() => dom.closeModal());
    dom.onBackBtn(() => this.#onBack());
    dom.onNextBtn(() => this.#onNext());
    dom.onOptionItemClick(($item) => this.#onOptionClick($item));
    dom.onReviewEditBtnClick((step) => this.#onReviewEdit(step));
    dom.onRemoveFromCart(() => this.#stepManager.reset());

    // AR button and Dimensions button
    const arBtn = dom.getARButton();

    if (arBtn) {
      arBtn.addEventListener("click", () => this.#threeDService.toggleAR());
    }

    const dimensionsBtn = dom.getDimensionsButton();
    console.log("dimensionsBtn =", dimensionsBtn);

    if (dimensionsBtn) {
      console.log("Attaching listener");

      dimensionsBtn.addEventListener("click", () => {
        console.log("CLICK!");
        this.#threeDService.toggleDimensions();
      });
    } else {
      console.log("Dimensions button NOT found");
    }

    // Re-bind cart remove buttons whenever the cart drawer re-renders
    document.addEventListener("cart:updated", () => {
      dom.rebindRemoveFromCart(() => this.#stepManager.reset());
    });

    // Any external subscriber can listen to selection changes via the store
    this.#selectionStore.addEventListener("change", ({ detail }) => {
      this.#threeDService.syncVariants(detail.variantIds);
    });
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  #onOpenModal() {
    this.#dom.openModal();
    this.#refreshAll();
    // Skip URL update on open when ar=true — wait for user to change a selection
    if (!this.#arMode) {
      const { mainVariantId, variantIds } = this.#selectionStore;
      this.#dom.updateURL(mainVariantId, variantIds);
    }
  }

  #onBack() {
    this.#stepManager.back();
    this.#applyStepSkip("back");
    this.#syncStepUI();
  }

  #onNext() {
    const sm = this.#stepManager;
    if (sm.isLast) {
      this.#addToCart();
      return;
    }
    sm.advance();
    if (sm.isLast) this.#buildReviewModel();
    this.#applyStepSkip("next");
    this.#syncStepUI();
  }

  #onOptionClick($item) {
    this.#dom.setChecked($item);
    this.#mappingEngine.applyAll(this.#productsData, this.#dom);
    this.#applyCompatibilityRules();
    this.#updateOptionPrices();
    this.#computeAndPublishSelections();
    this.#updateTotalPriceDisplay();
    // Always update URL on selection change — ar mode is no longer a restriction
    // once the user has interacted
    this.#arMode = false;
    const { mainVariantId, variantIds } = this.#selectionStore;
    this.#dom.updateURL(mainVariantId, variantIds);
  }

  #onReviewEdit(step) {
    this.#stepManager.jumpTo(step);
    $("div[customiser-product-container-step]").addClass("hidden");
    $(
      `div[customiser-product-container-step][customizer-product='true'][customizer-step='${step}']`
    ).removeClass("hidden");
    this.#syncStepUI();
  }

  // ── Step transitions ──────────────────────────────────────────────────────

  /**
   * Recursively skip steps whose product containers are hidden.
   * @param {'back'|'next'} direction
   */
  #applyStepSkip(direction) {
    const sm = this.#stepManager;
    if (this.#dom.isStepHidden(sm.current)) {
      direction === "back" ? sm.back() : sm.advance();
      this.#applyStepSkip(direction);
    } else {
      this.#dom.showStep(sm.current);
    }
  }

  #syncStepUI() {
    const sm = this.#stepManager;
    const texts = this.#nextBtnTexts;

    if (sm.isLast) {
      this.#dom.setNextBtnText(texts.addToCart);
    } else if (sm.isPreLast) {
      this.#dom.setNextBtnText(texts.review);
    } else {
      this.#dom.setNextBtnText(texts.next);
    }

    sm.isFirst ? this.#dom.hideBackBtn() : this.#dom.showBackBtn();
    this.#updateTitle();
  }

  #updateTitle() {
    const sm = this.#stepManager;
    const dom = this.#dom;
    const prefix = window.custom_opt?.["step_title_prefix"] ?? "";

    if (sm.isLast) {
      dom.setTitle(dom.getReviewListTitle());
      return;
    }

    const titles = dom.getStepTitles(sm.current);
    const joined = titles.join(" & ");
    dom.setTitle(prefix ? `${prefix} ${joined}` : joined);
  }

  // ── Selection computation ─────────────────────────────────────────────────

  /**
   * Read the DOM, derive selected variant ids, push to the store.
   */
  #computeAndPublishSelections() {
    const ids = [];
    let mainId = null;

    this.#dom.getProductContainers().forEach(({ productId, $el }) => {
      const selectedOptions = this.#dom.getSelectedOptionsForProduct($el);
      const product = this.#productsData.find(
        (p) => p.id.toString() === productId
      );
      if (!product) return;
      const variantId = this.#resolver.findIdByOptions(
        product.variants,
        selectedOptions
      );
      if (product.main_product) mainId = variantId;
      if (variantId) ids.push(variantId);
    });

    this.#selectionStore.update(ids, mainId);
  }

  // ── Compatibility rules ───────────────────────────────────────────────────

  #applyCompatibilityRules() {
    this.#computeAndPublishSelections();
    const { variantIds } = this.#selectionStore;

    $(
      "div[customiser-product-container][customizer-product='true'][product_not_compatible_with_variants]"
    ).each((_i, el) => {
      const $el = $(el);
      const incompatible = $el.attr("product_not_compatible_with_variants");
      const conflict = variantIds.find((id) => incompatible.includes(id));
      if (conflict) {
        this.#dom.hideProduct($el);
      } else if ($el.hasClass("hidden")) {
        this.#dom.showProduct($el, this.#stepManager.current);
      }
    });
  }

  // ── Price ─────────────────────────────────────────────────────────────────

  #updateTotalPriceDisplay() {
    const { variantIds } = this.#selectionStore;
    if (!variantIds.length) return;

    const variants = variantIds
      .map((id) => this.#resolver.findVariantGlobally(this.#productsData, id))
      .filter(Boolean);

    const regular = this.#priceCalc.calcRegular(variants);
    const compare = this.#priceCalc.calcCompareAt(variants);
    if (!regular) return;

    const fmt = window.formatMoney2;
    if (!fmt) return;

    this.#dom.setTotalPrice(
      fmt(regular),
      regular !== compare ? fmt(compare) : null
    );
  }

  #updateOptionPrices() {
    this.#dom.getProductContainers().forEach(({ productId, $el }) => {
      const product = this.#productsData.find(
        (p) => p.id.toString() === productId
      );
      if (!product) return;
      const selectedOptions = this.#dom.getSelectedOptionsForProduct($el);
      this.#dom.updateOptionItemPrices(
        productId,
        product.variants,
        selectedOptions,
        product.options?.length ?? 0
      );
    });
  }

  // ── Review model ──────────────────────────────────────────────────────────

  #buildReviewModel() {
    this.#dom
      .getProductContainers()
      .forEach(({ productId, step, crossLevel, isHidden, $el }) => {
        const product = this.#productsData.find(
          (p) => p.id.toString() === productId
        );
        if (!product) return;

        const isSkipped = !!$(
          `div[customiser-product-container][customizer-product='true'][product-id='${productId}'][hidden-product='true']`
        ).length;
        const isCrossHidden = crossLevel === "2" && isHidden;

        const fieldsets = this.#dom.getOptionFieldsets($el);
        const selectedOptions = fieldsets
          .map((f) => f.dataValue)
          .filter(Boolean);
        const variantData = this.#resolver.findByOptions(
          product.variants,
          selectedOptions
        );
        const forceHide = !!variantData?.variant_no_data;

        fieldsets.forEach(({ optionName, dataValue, $fieldset: _fs }) => {
          const isMapped =
            product.mapping?.some(
              (m) => m.cross_product_variant_name === optionName
            ) ?? false;
          const hide = isMapped || isCrossHidden || isSkipped || forceHide;
          this.#dom.updateReviewListItem(
            productId,
            optionName,
            step,
            dataValue,
            hide
          );
        });
      });

    this.#computeAndPublishSelections();
  }

  // ── Cart ──────────────────────────────────────────────────────────────────

  async #addToCart() {
    const { variantIds, mainVariantId } = this.#selectionStore;
    await this.#cartService.addToCart(
      variantIds,
      mainVariantId,
      this.#productsData,
      this.#resolver,
      (_response) => {
        this.#dom.closeModal();
        if (window.Alpine?.store("global")) {
          window.Alpine.store("global").isMinicartVisible = true;
        }
      },
      (_response) => {}
    );
  }

  // ── Full refresh ──────────────────────────────────────────────────────────

  #refreshAll() {
    this.#dom.showStep(this.#stepManager.current);
    this.#mappingEngine.applyAll(this.#productsData, this.#dom);
    this.#applyCompatibilityRules();
    this.#updateOptionPrices();
    this.#computeAndPublishSelections();
    this.#updateTotalPriceDisplay();
    this.#syncStepUI();

    // 3D init after short delay (configurator element may not be ready yet)
    setTimeout(() => {
      const { variantIds } = this.#selectionStore;
      this.#threeDService.setInitialVariants(variantIds);
      this.#threeDService.syncVariants(variantIds);
    }, 400);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Bootstrap — compose and launch
// ─────────────────────────────────────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", () => {
  // products is a global provided by Shopify Liquid
  const productsData = products; // eslint-disable-line no-undef
  const dom = new DomController();

  const app = new CustomizerApp({
    productsData,
    stepManager: new StepManager(productsData.total_steps),
    selectionStore: new SelectionStore(),
    dom,
    resolver: new VariantResolver(),
    mappingEngine: new MappingEngine(new DefaultMappingStrategy()),
    priceCalc: new PriceCalculator(),
    cartService: new CartService(),
    threeDService: new ThreeDService(dom.getConfigurator()),
  });

  app.init();

  /*
   * Toggle the "Dimensions" button label based on the viewport.
   * Desktop (>=768px): show ruler icon + "Dimensions"
   * Mobile (<768px): show ruler icon only.
   */
  const DESKTOP_BREAKPOINT = 768;

  function toggleDimensionsButtonLabel() {
    document.querySelectorAll(".dimensions-label").forEach((label) => {
      label.style.display =
        window.innerWidth >= DESKTOP_BREAKPOINT ? "inline" : "none";
    });
  }

  toggleDimensionsButtonLabel();

  // Update when the viewport changes
  window.addEventListener("resize", toggleDimensionsButtonLabel);

  console.log(
    "------------------------------ CUSTOMISER OOP JS LOADED ------------------------------"
  );
});


